/**
 * The rank ladder's arithmetic, kept out of the component.
 *
 * Two functions, both total, both about numbers that are wrong in ways a
 * screenshot cannot show — the reason `pan-zoom.ts` and `daily-quest-box.ts` are
 * their own modules too. A progress bar drawn at 100% when the divisor is null
 * and one drawn at 100% because the player really is at the top of the ladder
 * photograph identically.
 *
 * No Vue, no DOM, no clock: the component decides *when* to ask, this decides
 * what the answer is.
 */

/** The wallet fields this module needs — a structural subset of `Wallet`. */
export interface RankStanding {
  xpIntoRank: number;
  /** The current rank's whole span, or `null` at the top of the ladder. */
  xpForNextRank: number | null;
}

/**
 * How far into the current rank the player is, 0–1, or `null` with no wallet.
 *
 * `xpForNextRank` is the current rank's **whole span**, not a cumulative target
 * — the distinction that once produced a bar reading `2,965 / −25,101 XP` on the
 * lobby panel, because subtracting the rank's start from a span gives a negative
 * denominator. Here it is used as the divisor directly, and the three edge cases
 * are the ones that bar got wrong:
 *
 * - `null` span means the top rank: a **full** bar, not a division by zero.
 * - a non-positive span cannot be divided by, so it reads empty rather than
 *   `Infinity` or `NaN` (either of which renders as a bar of no width and no
 *   error, which is the worst way to be wrong).
 * - the ratio is clamped, because `xpIntoRank` is the server's and this must not
 *   draw past the end of its track if the two ever disagree by a unit.
 */
export function rankProgress(standing: RankStanding | null): number | null {
  if (!standing) return null;
  if (standing.xpForNextRank === null) return 1;
  if (standing.xpForNextRank <= 0) return 0;
  if (!Number.isFinite(standing.xpIntoRank)) return 0;
  return Math.min(1, Math.max(0, standing.xpIntoRank / standing.xpForNextRank));
}

/**
 * Where a rank's insignia lives, derived from its number.
 *
 * `/assets/ranks/rank-07.webp` — zero-padded to two digits, the same convention
 * a map's picture and a hull part's follow. A path is not a fact worth sending,
 * and a served path would be a second place for the naming rule to live: a
 * twenty-first rank is a file on disk and nothing else.
 *
 * Pure and total so the convention is pinned by a test rather than by whichever
 * ranks happen to have art today. The *missing file* case is the component's —
 * it needs an `@error` from a real `<img>` to know, and answers it with the
 * numbered plate rather than a broken image.
 */
export function rankArtPath(rank: number): string {
  const n = Math.max(0, Math.floor(rank));
  return `/assets/ranks/rank-${String(n).padStart(2, '0')}.webp`;
}
